import { RouteLocationNormalizedLoaded as Route } from 'vue-router';
import { SitemapNode } from '@/plugins/sitemap';

export interface NavigationEntry {
    route: Route;
    sitemapNode: SitemapNode;
    timestamp: number;
}

export interface BreadcrumbEntry {
    node: SitemapNode;
    route: Route;
    isCurrent: boolean;
}

export class NavigationHistory
{
    private history: NavigationEntry[] = [];
    private maxHistorySize: number = 50;

    /**
     * Add a new navigation entry to the history
     */
    public addEntry(route: Route, sitemapNode: SitemapNode, isSitemapNavigation: boolean = false): void
    {
        const entry: NavigationEntry = {
            route,
            sitemapNode,
            timestamp: Date.now()
        };

        // If this is sitemap navigation, clear history and don't add to it
        if (isSitemapNavigation)
        {
            this.clearHistory();

            return;
        }

        // Check if this is a backward navigation (user went back)
        const isBackwardNavigation = this.isBackwardNavigation(route);

        if (isBackwardNavigation)
        {
            // Remove entries after the matching route
            this.removeEntriesAfterRoute(route);
        }
        else
        {
            // Check if this is a cross-module navigation
            const isCrossModuleNavigation = this.isCrossModuleNavigation(route);

            if (isCrossModuleNavigation)
            {
                // Clear history when navigating to a different module
                this.clearHistory();
            }

            // Add new entry
            this.history.push(entry);

            // Maintain history size limit
            if (this.history.length > this.maxHistorySize)
            {
                this.history = this.history.slice(-this.maxHistorySize);
            }
        }
    }

    /**
     * Get breadcrumbs based on navigation history and current route
     */
    public getBreadcrumbs(currentRoute: Route, currentSitemapNode: SitemapNode): BreadcrumbEntry[]
    {
        const breadcrumbs: BreadcrumbEntry[] = [];

        // Special handling for module-form-details route
        if (currentRoute.name === 'module-form-details')
        {
            // For this route, we'll use navigation history but with enhanced duplicate prevention
            const moduleNode = this.getModuleNode(currentSitemapNode);

            // Always start with module breadcrumb
            if (moduleNode && moduleNode !== currentSitemapNode)
            {
                breadcrumbs.push({
                    node: moduleNode,
                    route: currentRoute,
                    isCurrent: false
                });
            }

            // Add navigation history entries, but filter carefully
            if (this.history.length > 0)
            {
                const seenNodes = new Set<string>();

                if (moduleNode) seenNodes.add(moduleNode.name);

                this.history.forEach(entry =>
                {
                    // Skip module nodes and duplicates, but include list views
                    if (!seenNodes.has(entry.sitemapNode.name) &&
                        entry.sitemapNode !== moduleNode &&
                        entry.sitemapNode.name !== currentSitemapNode.name)
                    {
                        breadcrumbs.push({
                            node: entry.sitemapNode,
                            route: entry.route,
                            isCurrent: false
                        });
                        seenNodes.add(entry.sitemapNode.name);
                    }
                });
            }

            // Add current entry
            breadcrumbs.push({
                node: currentSitemapNode,
                route: currentRoute,
                isCurrent: true
            });

            return breadcrumbs;
        }

        // Always start with module breadcrumb from sitemap hierarchy
        const moduleNode = this.getModuleNode(currentSitemapNode);

        if (moduleNode && moduleNode !== currentSitemapNode)
        {
            breadcrumbs.push({
                node: moduleNode,
                route: currentRoute, // We don't have historical route for module nodes
                isCurrent: false
            });
        }

        // If we have navigation history, use it (but skip duplicates)
        if (this.history.length > 0)
        {
            // Add historical entries, but filter out duplicates and module nodes
            const seenNodes = new Set<string>();

            if (moduleNode) seenNodes.add(moduleNode.name);

            this.history.forEach(entry =>
            {
                const nodeKey = `${entry.sitemapNode.name}:${String(entry.route.name)}`;

                // Skip if we've already seen this node or if it's the module node
                // Also skip if this is the same as the current node
                if (!seenNodes.has(entry.sitemapNode.name) &&
                    entry.sitemapNode !== moduleNode &&
                    entry.sitemapNode.name !== currentSitemapNode.name)
                {
                    breadcrumbs.push({
                        node: entry.sitemapNode,
                        route: entry.route,
                        isCurrent: false
                    });
                    seenNodes.add(entry.sitemapNode.name);
                }
            });
        }
        else
        {
            // Fallback to sitemap hierarchy (excluding module node which we already added)
            const sitemapPath = this.getSitemapPath(currentSitemapNode);
            const seenNodes = new Set<string>();

            if (moduleNode) seenNodes.add(moduleNode.name);

            sitemapPath.forEach(node =>
            {
                if (!seenNodes.has(node.name) &&
                    node !== moduleNode &&
                    node.name !== currentSitemapNode.name)
                {
                    breadcrumbs.push({
                        node,
                        route: currentRoute, // We don't have historical route for sitemap nodes
                        isCurrent: false
                    });
                    seenNodes.add(node.name);
                }
            });
        }

        // Add current entry only if it's not already in the breadcrumbs
        const currentNodeName = currentSitemapNode.name;
        const hasCurrentNode = breadcrumbs.some(b =>
            b.node.name === currentNodeName &&
            this.routesMatch(b.route, currentRoute)
        );

        if (!hasCurrentNode)
        {
            breadcrumbs.push({
                node: currentSitemapNode,
                route: currentRoute,
                isCurrent: true
            });
        }
        else
        {
            // Mark the existing entry as current
            const existingIndex = breadcrumbs.findIndex(b =>
                b.node.name === currentNodeName &&
                this.routesMatch(b.route, currentRoute)
            );

            if (existingIndex !== -1)
            {
                breadcrumbs[existingIndex].isCurrent = true;
            }
        }

        // Final deduplication pass - remove any remaining duplicates
        const finalBreadcrumbs: BreadcrumbEntry[] = [];
        const seenNames = new Set<string>();

        breadcrumbs.forEach(entry =>
        {
            const nameKey = entry.node.name;

            if (!seenNames.has(nameKey))
            {
                finalBreadcrumbs.push(entry);
                seenNames.add(nameKey);
            }
        });

        return finalBreadcrumbs;
    }

    /**
     * Check if the current navigation is backward (user went back)
     */
    private isBackwardNavigation(route: Route): boolean
    {
        if (this.history.length === 0) return false;

        // Check if this route already exists in our history
        const existingIndex = this.history.findIndex(entry =>
            this.routesMatch(entry.route, route)
        );

        return existingIndex !== -1;
    }

    /**
     * Check if this is a cross-module navigation
     */
    private isCrossModuleNavigation(route: Route): boolean
    {
        if (this.history.length === 0) return false;

        const currentModule = this.getModuleFromRoute(route);
        const lastModule = this.getModuleFromRoute(this.history[this.history.length - 1].route);

        return currentModule !== lastModule;
    }

    /**
     * Remove entries after a specific route (for backward navigation)
     */
    private removeEntriesAfterRoute(route: Route): void
    {
        const existingIndex = this.history.findIndex(entry =>
            this.routesMatch(entry.route, route)
        );

        if (existingIndex !== -1)
        {
            this.history = this.history.slice(0, existingIndex + 1);
        }
    }

    /**
     * Get sitemap path for a node (fallback method)
     */
    private getSitemapPath(node: SitemapNode): SitemapNode[]
    {
        const path: SitemapNode[] = [];
        let current = node;

        while (current && current.parent)
        {
            path.unshift(current.parent);
            current = current.parent;
        }

        return path;
    }

    /**
     * Get the module node for a given sitemap node
     */
    private getModuleNode(node: SitemapNode): SitemapNode | null
    {
        // Find the top-level module node by traversing up the parent chain
        let current = node;

        while (current && current.parent)
        {
            current = current.parent;
        }

        // Return the top-level node (module node)
        // Only return if it's different from the current node
        return current && current !== node ? current : null;
    }

    /**
     * Compare two routes to see if they match
     */
    private routesMatch(route1: Route, route2: Route): boolean
    {
        // Compare route name and params
        if (route1.name !== route2.name) return false;

        // Compare params
        const params1 = route1.params || {};
        const params2 = route2.params || {};

        const paramKeys1 = Object.keys(params1);
        const paramKeys2 = Object.keys(params2);

        if (paramKeys1.length !== paramKeys2.length) return false;

        for (const key of paramKeys1)
        {
            if (params1[key] !== params2[key]) return false;
        }

        return true;
    }

    /**
     * Extract module from route (you may need to adjust this based on your routing structure)
     */
    private getModuleFromRoute(route: Route): string
    {
        // This is a simplified implementation - you may need to adjust based on your routing structure
        const routeName = route.name as string;

        if (!routeName) return 'unknown';

        // Extract module from route name (e.g., 'core-users-list' -> 'core')
        const parts = routeName.split('-');

        return parts[0] || 'unknown';
    }

    /**
     * Clear navigation history
     */
    public clearHistory(): void
    {
        this.history = [];
    }

    /**
     * Get current history (for debugging)
     */
    public getHistory(): NavigationEntry[]
    {
        return [...this.history];
    }

    /**
     * Debug method to log current state
     */
    public debugState(currentRoute: Route, currentSitemapNode: SitemapNode): void
    {
        console.log('=== NavigationHistory Debug ===');
        console.log('Current route:', currentRoute.name);
        console.log('Current sitemap node:', currentSitemapNode.name);
        console.log('History length:', this.history.length);
        console.log('History entries:', this.history.map(h => h.sitemapNode.name));

        const moduleNode = this.getModuleNode(currentSitemapNode);

        console.log('Module node:', moduleNode?.name || 'null');

        const breadcrumbs = this.getBreadcrumbs(currentRoute, currentSitemapNode);

        console.log('Generated breadcrumbs:', breadcrumbs.map(b => b.node.name));

        // Show if this is sitemap navigation (from navigation store)
        console.log('Sitemap navigation: Check navigation store for current status');

        // Special debugging for module-form-details route
        if (currentRoute.name === 'module-form-details')
        {
            console.log('=== Special Debug for module-form-details ===');
            console.log('Current sitemap node details:', {
                name: currentSitemapNode.name,
                route: currentSitemapNode.route,
                parent: currentSitemapNode.parent?.name,
                hasParent: !!currentSitemapNode.parent
            });

            // Log the full sitemap path
            const sitemapPath = this.getSitemapPath(currentSitemapNode);

            console.log('Sitemap path:', sitemapPath.map(n => n.name));

            // Log all breadcrumb entries with more details
            breadcrumbs.forEach((entry, index) =>
            {
                console.log(`Breadcrumb ${index}:`, {
                    name: entry.node.name,
                    route: entry.route.name,
                    isCurrent: entry.isCurrent,
                    nodeRoute: entry.node.route
                });
            });
        }

        console.log('==============================');
    }
}
